/**
 * SalesAnalysisReport.jsx — R8
 * Route: /pos/reports/sales-analysis
 * API:   GET /api/pos/reports/sales-analysis
 *
 * Multi-dimension analysis in one page. Five panels:
 *   1. KPI headline cards
 *   2. Order-type revenue split (table)
 *   3. Hourly heatmap (table with colored cells)
 *   4. Payment mode mix (table)
 *   5. Top-10 items by revenue (table)
 *   6. Daily trend (table)
 *
 * Download downloads all panels merged into one CSV.
 * Print/PDF sends to PrintReportPreview.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Spin, Tabs } from "antd";
import { Download, Calendar, BarChart2, TrendingUp, Clock, ShoppingCart, CreditCard } from "react-feather";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import SelectComponent from "react-select";
import PrintReportPreview from "../../reports/PrintReportPreview";

const getToken    = () => localStorage.getItem("token");
const getApiUrl = () => {
  const base = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace(/\/+$/, "");
  return base; //.endsWith("/pos") ? base : `${base}/pos`;
};
const getCurrency = () => localStorage.getItem("currencycode") || "₹";
const BUSINESS_DATE_KEYS = ["posbusiness_date", "business_date", "pos_business_date"];
const getBizDate = () => {
  for (const key of BUSINESS_DATE_KEYS) {
    const d = localStorage.getItem(key);
    if (d && moment(d).isValid()) return moment(d).startOf("day");
  }
  return getBizDate().clone().startOf("day");
};
const downloadCSV = (columns,data,filename)=>{
  const h=columns.filter(c=>c.dataIndex).map(c=>c.title).join(",");
  const r=data.map(row=>columns.filter(c=>c.dataIndex).map(c=>`"${String(row[c.dataIndex]??"").replace(/"/g,'""')}"`).join(","));
  const blob=new Blob([[h,...r].join("\n")],{type:"text/csv;charset=utf-8;"}); const url=URL.createObjectURL(blob);
  const a=document.createElement("a"); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
};

// KPI stat card style
const KpiCard = ({ icon, label, value, sub, color }) => (
  <div style={{ background:"#fff", border:`1px solid ${color}22`, borderRadius:16,
    padding:"14px 16px", boxShadow:`0 6px 18px ${color}11`, flex:1, minWidth:160 }}>
    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
      <span style={{width:36,height:36,borderRadius:12,background:`${color}15`,color,
        display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{icon}</span>
      <span style={{fontSize:12,fontWeight:800,color:"#64748b"}}>{label}</span>
    </div>
    <div style={{fontSize:22,fontWeight:950,color:"#0f172a",lineHeight:1.2}}>{value}</div>
    {sub && <div style={{fontSize:11,color:"#94a3b8",marginTop:4}}>{sub}</div>}
  </div>
);

const SalesAnalysisReport = () => {
  const currency=getCurrency(); const apiURL=getApiUrl();
  const [dateRange,     setDateRange]     = useState({startDate:getBizDate().clone().subtract(6,"days"),endDate:getBizDate()});
  const [activeTab,     setActiveTab]     = useState("order_type");
  const [loading,       setLoading]       = useState(false);
  const [data,          setData]          = useState({ kpi:{}, orderTypeSplit:[], hourlyHeatmap:[], paymentMix:[], topItems:[], dailyTrend:[] });
  const [previewVisible,setPreviewVisible]= useState(false);
  const [exportData,    setExportData]    = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [compFilter,    setCompFilter]    = useState({value:"false",label:"Exclude Complementary"});
  const [orderTypeFilter,setOrderTypeFilter]=useState(null);

  const compOptions     = [{value:"false",label:"Exclude Complementary"},{value:"true",label:"Include Complementary"}];
  const orderTypeOptions= ["Table Service","Room Service","Delivery","Counter"].map(v=>({value:v,label:v}));

  const fetchReport=useCallback(async()=>{
    setLoading(true);
    try{
      const params={
        start_date:dateRange.startDate.format("YYYY-MM-DD"),
        end_date:dateRange.endDate.format("YYYY-MM-DD"),
        is_complementary:compFilter?.value||"false",
        ...(orderTypeFilter&&{order_type:orderTypeFilter.value}),
      };
      const res=await axios.get(`${apiURL}/pos/sales-analysis`,{headers:{Authorization:`Bearer ${getToken()}`},params});
      if(res.data.success) setData({ kpi:res.data.kpi||{}, orderTypeSplit:res.data.orderTypeSplit||[],
        hourlyHeatmap:res.data.hourlyHeatmap||[], paymentMix:res.data.paymentMix||[],
        topItems:res.data.topItems||[], dailyTrend:res.data.dailyTrend||[] });
    }catch(err){Swal.fire({icon:"error",title:"Error",text:err?.response?.data?.error||err.message});}
    finally{setLoading(false);}
  },[dateRange,compFilter,orderTypeFilter,apiURL]);

  useEffect(()=>{fetchReport();},[fetchReport]);

  // ── Column definitions per tab ─────────────────────────────────────────────

  const orderTypeCols=[
    {title:"Order Type",dataIndex:"order_type",width:160},
    {title:"Bills",     dataIndex:"bill_count", width:80, align:"right"},
    {title:`Revenue (${currency})`,dataIndex:"revenue",width:140,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:"% of Total",dataIndex:"pct_of_total",width:100,align:"right",render:v=>`${Number(v||0).toFixed(1)}%`},
  ];

  const hourlyCols=[
    {title:"Hour",      dataIndex:"hour_of_day",  width:80,  render:v=>`${String(v).padStart(2,"0")}:00`},
    {title:"Bills",     dataIndex:"bill_count",   width:80,  align:"right"},
    {title:`Revenue (${currency})`,dataIndex:"revenue",width:130,align:"right",
      render:(v,_,i)=>{
        const max=Math.max(...data.hourlyHeatmap.map(r=>Number(r.revenue||0)));
        const pct=max>0?Number(v||0)/max:0;
        const bg=`rgba(37,99,235,${(0.1+pct*0.55).toFixed(2)})`;
        return <span style={{background:bg,padding:"2px 8px",borderRadius:6,fontWeight:700,fontSize:13}}>{Number(v||0).toFixed(2)}</span>;
      }
    },
    {title:`Avg Bill (${currency})`,dataIndex:"avg_bill",width:120,align:"right",render:v=>Number(v||0).toFixed(2)},
  ];

  const paymentCols=[
    {title:"Payment Mode",  dataIndex:"payment_mode",     width:140},
    {title:"Bills",         dataIndex:"bill_count",       width:80,  align:"right"},
    {title:`Collected (${currency})`,dataIndex:"collected_amount",width:140,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:"% of Total",    dataIndex:"pct_of_total",     width:100, align:"right",render:v=>`${Number(v||0).toFixed(1)}%`},
  ];

  const topItemCols=[
    {title:"#",         dataIndex:"_rank",          width:50,  render:(_,__,i)=>i+1},
    {title:"Item Name", dataIndex:"item_name",       width:220},
    {title:"Qty Sold",  dataIndex:"qty_sold",        width:90,  align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Revenue (${currency})`,dataIndex:"revenue",width:130,align:"right",fixed:"right",
      render:v=><strong style={{color:"#166534"}}>{Number(v||0).toFixed(2)}</strong>},
    {title:"Orders",    dataIndex:"order_count",     width:80,  align:"right"},
  ];

  const trendCols=[
    {title:"Date",       dataIndex:"date_label",     width:120},
    {title:"Bills",      dataIndex:"bill_count",     width:80,  align:"right"},
    {title:`Revenue (${currency})`,dataIndex:"revenue",width:130,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Tax (${currency})`,dataIndex:"tax_collected",width:110,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Avg Bill (${currency})`,dataIndex:"avg_bill",width:120,align:"right",render:v=>Number(v||0).toFixed(2)},
  ];

  const tabData = {
    order_type: { cols:orderTypeCols, data:data.orderTypeSplit, label:"Order Type Split" },
    hourly:     { cols:hourlyCols,    data:data.hourlyHeatmap,  label:"Hourly Heatmap" },
    payment:    { cols:paymentCols,   data:data.paymentMix,     label:"Payment Mix" },
    top_items:  { cols:topItemCols,   data:data.topItems,       label:"Top 10 Items" },
    daily:      { cols:trendCols,     data:data.dailyTrend,     label:"Daily Trend" },
  };

  const current = tabData[activeTab] || tabData.order_type;

  const kpi = data.kpi;
  const days = Number(kpi.days_in_range||1);

  return(
    <div className="page-wrapper"><div className="content container-fluid">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="row align-items-center mb-3">
        <div className="col-auto d-flex align-items-center gap-3">
          <h2 style={{fontSize:20,fontWeight:700,borderBottom:"2px solid #007bff",paddingBottom:4,margin:0}}>Sales Analysis</h2>
          <DateRangePicker
            key={`${dateRange.startDate.format("YYYY-MM-DD")}-${dateRange.endDate.format("YYYY-MM-DD")}`}
            initialSettings={{startDate:dateRange.startDate.toDate(),endDate:dateRange.endDate.toDate(),
              ranges:{Today:[getBizDate().clone().startOf("day").toDate(),getBizDate().clone().endOf("day").toDate()],
                "Last 7 Days":[getBizDate().clone().subtract(6,"days").startOf("day").toDate(),getBizDate().clone().endOf("day").toDate()],
                "This Month":[getBizDate().clone().startOf("month").toDate(),getBizDate().clone().endOf("month").toDate()],
                "Last Month":[getBizDate().clone().subtract(1,"month").startOf("month").toDate(),getBizDate().clone().subtract(1,"month").endOf("month").toDate()]},
              locale:{format:"DD MMM YYYY",applyLabel:"Apply",cancelLabel:"Cancel"},alwaysShowCalendars:true,autoApply:false}}
            onApply={(_,p)=>setDateRange({startDate:p.startDate,endDate:p.endDate})}>
            <div className="input-group" style={{width:270}}>
              <input type="text" className="form-control text-center fw-bold" style={{height:38,fontSize:14,borderRight:"none",background:"#fff"}}
                value={`${dateRange.startDate.format("DD MMM YYYY")} – ${dateRange.endDate.format("DD MMM YYYY")}`} readOnly/>
              <span className="input-group-text" style={{background:"#fff",borderLeft:"none",height:38,cursor:"pointer"}}><Calendar size={15} color="#495057"/></span>
            </div>
          </DateRangePicker>
        </div>
        <div className="col-auto ms-auto d-flex align-items-center gap-2">
          <div style={{width:200}}>
            <SelectComponent options={compOptions} value={compFilter} onChange={setCompFilter} styles={{control:b=>({...b,fontSize:13,height:38})}}/>
          </div>
          <div style={{width:180}}>
            <SelectComponent isClearable placeholder="All Order Types" options={orderTypeOptions}
              value={orderTypeFilter} onChange={setOrderTypeFilter} styles={{control:b=>({...b,fontSize:13,height:38})}}/>
          </div>
          <div className="dropdown">
            <Button type="primary" className="d-flex align-items-center dropdown-toggle" data-bs-toggle="dropdown" style={{height:38}}>
              <Download size={15} className="me-2"/>Download
            </Button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><button className="dropdown-item" onClick={()=>downloadCSV(current.cols,current.data,
                  `Sales_Analysis_${activeTab}_${dateRange.startDate.format("YYYYMMDD")}.csv`)}>
                Download Current Tab CSV
              </button></li>
              <li><button className="dropdown-item" onClick={()=>{setExportData(current.data);
                  setExportHeaders(current.cols.filter(c=>c.dataIndex).map(c=>({label:c.title,key:c.dataIndex})));
                  setPreviewVisible(true);}}>
                Print / PDF Preview
              </button></li>
            </ul>
          </div>
        </div>
      </div>

      {/* ── KPI cards ───────────────────────────────────────────────── */}
      {loading ? <div className="text-center my-5"><Spin size="large"/></div> : (
        <>
          <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:20}}>
            <KpiCard icon={<BarChart2 size={18}/>} label="Total Revenue"
              value={`${currency} ${Number(kpi.total_revenue||0).toLocaleString("en-IN",{maximumFractionDigits:0})}`}
              sub={`${Number(kpi.total_bills||0)} bills · ${days} day(s)`} color="#1d4ed8"/>
            <KpiCard icon={<ShoppingCart size={18}/>} label="Avg Bill Value"
              value={`${currency} ${Number(kpi.avg_bill_value||0).toFixed(2)}`}
              sub="Per order average" color="#0f766e"/>
            <KpiCard icon={<TrendingUp size={18}/>} label="Net Subtotal"
              value={`${currency} ${Number(kpi.total_subtotal||0).toLocaleString("en-IN",{maximumFractionDigits:0})}`}
              sub="Pre-tax revenue" color="#7c3aed"/>
            <KpiCard icon={<CreditCard size={18}/>} label="Tax Collected"
              value={`${currency} ${Number(kpi.total_tax||0).toLocaleString("en-IN",{maximumFractionDigits:0})}`}
              sub="Across all tax types" color="#b45309"/>
            <KpiCard icon={<Clock size={18}/>} label="Daily Average"
              value={`${currency} ${(Number(kpi.total_revenue||0)/days).toLocaleString("en-IN",{maximumFractionDigits:0})}`}
              sub={`Over ${days} days`} color="#0369a1"/>
          </div>

          {/* ── Tabs ──────────────────────────────────────────────────── */}
          <Tabs activeKey={activeTab} onChange={setActiveTab} style={{marginBottom:12}}
            items={[
              {key:"order_type", label:"Order Type Split"},
              {key:"hourly",     label:"Hourly Heatmap"},
              {key:"payment",    label:"Payment Mix"},
              {key:"top_items",  label:"Top 10 Items"},
              {key:"daily",      label:"Daily Trend"},
            ]}/>

          <Table columns={current.cols} dataSource={current.data} rowKey={(_,i)=>i}
            scroll={{x:"max-content"}} size="small" bordered
            pagination={activeTab==="top_items"?false:{pageSize:50,showTotal:t=>`${t} rows`}}/>
        </>
      )}

      <PrintReportPreview visible={previewVisible} onClose={()=>setPreviewVisible(false)}
        reportTitle={`Sales Analysis — ${current.label} — ${dateRange.startDate.format("DD MMM YYYY")} to ${dateRange.endDate.format("DD MMM YYYY")}`}
        propertyName="" data={exportData} headers={exportHeaders} footerData="" totalColumns={[]} apiEndpoint="transactions/generate-pdf" apiUrl={apiURL}/>
    </div></div>
  );
};

export default SalesAnalysisReport;
